import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import "./customer.scss";
import CustomerOTPPage from "./CustomerOTPPage";
import { useAuthenticateCustomerMutation } from "../../../Services/eurocustomer/EuroCustomer";
import { Loader } from "react-bootstrap-typeahead";
import { toast } from "react-toastify";
import CustomerSIgnup from "./CustomerSIgnup";
import TermsAndConditionsModal from "./TermsAndConditionsModalComponent";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import {
    getTenantSubdomain,
    isAutoServicesTenant,
    TENANT_BRANDING_LOGOS,
} from "../../../utils/tenantBranding";

interface IPROPS {
    fetchVehicles: any;
    setIsOtpVerified: any;
    setOpenModal: any;
}

const CustomerLogin: React.FC<IPROPS> = ({
    fetchVehicles,
    setIsOtpVerified,
    setOpenModal,
}) => {
    const [isEmailSelected, setIsEmailSelected] = useState<boolean>(false);
    const [isOtpButtonClicked, setIsOtpButtonClicked] =
        useState<boolean>(false);
    const [input, setInput] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [signupPageOpen, setSignupPageOpen] = useState<boolean>(false);
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
    const [openPrivacyPolicyModal, setOpenPrivacyPolicyModal] = useState(false);

    // Resend OTP timer state
    const [resendTimer, setResendTimer] = useState(0);

    useEffect(() => {
        setInput("");
    }, [isEmailSelected]);

    const [authenticateTrigger, { status: authenticateStatus }] =
        useAuthenticateCustomerMutation();

    useEffect(() => {
        if (authenticateStatus === "pending") {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [authenticateStatus]);

    // Timer countdown effect for resend OTP
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [resendTimer]);

    // Email validation function
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Mobile number validation function
    const validateMobile = (mobile: string): boolean => {
        return /^[0-9]{10}$/.test(mobile);
    };

    const openTermsModal = (): void => {
        setIsTermsModalOpen(true);
    };

    const closeTermsModal = (): void => {
        setIsTermsModalOpen(false);
        setOpenPrivacyPolicyModal(false);
    };

    const handleAcceptTerms = (): void => {
        setAcceptTerms(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (isEmailSelected) {
            setInput(value);
        } else {
            // Remove non-digits and limit to 10 characters for mobile
            const val = value.replace(/\D/g, "").slice(0, 10);
            setInput(val);
        }
    };
    const fetchingTenant = getTenantSubdomain();
    const autoServicesBranding = isAutoServicesTenant(fetchingTenant);

    const submitHandler = async (): Promise<void> => {
        // Validate input based on selected tab
        if (isEmailSelected) {
            if (!input.trim()) {
                toast.error("Email address is required");
                return;
            }
            if (!validateEmail(input.trim())) {
                toast.error("Enter a valid email address.");
                return;
            }
        } else {
            if (!input.trim()) {
                toast.error("Mobile number is required");
                return;
            }
            if (!validateMobile(input.trim())) {
                toast.error("Enter a valid 10-digit mobile number");
                return;
            }
        }

        const payloadObj = {
            identifier: input.trim(),
            user_platform: "web",
        };

        try {
            const response = await authenticateTrigger(payloadObj);

            if (response?.data) {
                setIsOtpButtonClicked(true);
                toast.success("OTP sent successfully");
                // Reset resendTimer on success so user can resend from OTP page
                setResendTimer(0);
            } else if (response?.error) {
                const errorData = response.error as any;

                if (errorData?.data?.identifier) {
                    toast.error(errorData.data.identifier[0]);
                } else if (
                    errorData?.data?.error ===
                        "Please wait before requesting another OTP." &&
                    typeof errorData.data.retry_after === "number"
                ) {
                    setResendTimer(errorData.data.retry_after);
                    toast.error(
                        `Please wait ${errorData.data.retry_after} seconds before requesting another OTP.`
                    );
                } else if (errorData?.data?.message) {
                    toast.error(errorData.data.message);
                } else {
                    toast.error("Unable to send OTP. Please try again.");
                }
            }
        } catch (error) {
            console.error("Error sending OTP:", error);
            toast.error("Unable to send OTP");
        }
    };

    return (
        <div className="login-container" style={{ paddingTop: "0px" }}>
            {isOtpButtonClicked ? (
                <CustomerOTPPage
                    input={input}
                    setOpenLoginModal={""}
                    resendOtp={submitHandler}
                    fetchVehicles={fetchVehicles}
                    setIsOtpVerified={setIsOtpVerified}
                    setOpenModal={setOpenModal}
                    loading={authenticateStatus}
                    isEmailSelected={isEmailSelected}
                />
            ) : (
                <>
                    <div className="logo" style={{ marginBottom: "1rem" }}>

                    {autoServicesBranding ? (
          <img
            src={TENANT_BRANDING_LOGOS.autoServices}
            alt="Auto Services logo"
            style={{ width: "300px", height: "100px" }}
          />
        ) : (
                        <img
                            src={TENANT_BRANDING_LOGOS.eurorepar}
                            alt="EUROREPAR Logo"
                            style={{ width: "200px" }}
                        />
        )}
                    </div>

                    <p
                        className="welcome-text"
                        style={{
                            fontSize: "30px",
                            margin: "5px",
                            marginTop: "0",
                        }}
                    >
                        {autoServicesBranding ? "Welcome to Auto Services!" : "Welcome!"}
                    </p>

                    {signupPageOpen ? (
                        <CustomerSIgnup />
                    ) : (
                        <div className="login-box">
                            <h4 className="login-heading">
                                <span>Log In</span>
                            </h4>
                            <p className="login-subheading">
                                Log In with Email or Mobile Number
                            </p>

                            <div className="login-tabs">
                                <button
                                    className={`tab ${
                                        isEmailSelected ? "active" : ""
                                    }`}
                                    onClick={() => setIsEmailSelected(true)}
                                >
                                    Email
                                </button>
                                <button
                                    className={`tab ${
                                        !isEmailSelected ? "active" : ""
                                    }`}
                                    onClick={() => setIsEmailSelected(false)}
                                >
                                    Mobile Number
                                </button>
                            </div>

                            {/* Updated input fields with unique IDs */}
                            {isEmailSelected ? (
                                <div className="input-group">
                                    <label
                                        htmlFor={`login-email-${
                                            isEmailSelected
                                                ? "active"
                                                : "inactive"
                                        }`}
                                    >
                                        Email
                                    </label>
                                    <input
                                        value={input}
                                        onChange={handleInputChange}
                                        type="email"
                                        id={`login-email-${
                                            isEmailSelected
                                                ? "active"
                                                : "inactive"
                                        }`}
                                        name="login-email"
                                        placeholder="Enter Email Address"
                                        style={{ borderRadius: "50px" }}
                                    />
                                </div>
                            ) : (
                                <div className="input-group">
                                    <label
                                        htmlFor={`login-mobile-${
                                            !isEmailSelected
                                                ? "active"
                                                : "inactive"
                                        }`}
                                    >
                                        Mobile number
                                    </label>
                                    <input
                                        value={input}
                                        onChange={handleInputChange}
                                        type="tel"
                                        id={`login-mobile-${
                                            !isEmailSelected
                                                ? "active"
                                                : "inactive"
                                        }`}
                                        name="login-mobile"
                                        placeholder="Enter Mobile Number"
                                        style={{ borderRadius: "50px" }}
                                        maxLength={10}
                                    />
                                </div>
                            )}

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    marginBottom: "10px",
                                    marginLeft: "10px",
                                    fontFamily: "Karbon-regular",
                                    fontSize: "medium",
                                }}
                            >
                                <div style={{ textAlign: "center" }}>
                                    <span>By continuing, you agree to </span>
                                    <span
                                        onClick={() =>
                                            setOpenPrivacyPolicyModal(true)
                                        }
                                        style={{
                                            color: "#007bff",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "14px",
                                        }}
                                    >
                                        Privacy Policy
                                    </span>
                                </div>
                                &
                                <div style={{ textAlign: "center" }}>
                                    <span
                                        onClick={openTermsModal}
                                        style={{
                                            color: "#007bff",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "14px",
                                        }}
                                    >
                                        T&C
                                    </span>
                                </div>
                            </div>

                            <button
                                disabled={loading || resendTimer > 0}
                                className={
                                    loading ? "loading-state" : "send-otp"
                                }
                                onClick={submitHandler}
                                style={{
                                    opacity:
                                        loading || resendTimer > 0 ? 0.6 : 1,
                                    cursor:
                                        loading || resendTimer > 0
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                            >
                                {loading ? (
                                    <>
                                        Sending... <Loader />
                                    </>
                                ) : resendTimer > 0 ? (
                                    `Resend Code (${resendTimer}s)`
                                ) : (
                                    "Send OTP"
                                )}
                                
                            </button>
                        </div>
                    )}

                    <TermsAndConditionsModal
                        isOpen={isTermsModalOpen}
                        onRequestClose={closeTermsModal}
                        onAccept={handleAcceptTerms}
                    />

                    <PrivacyPolicyModal
                        isOpen={openPrivacyPolicyModal}
                        onRequestClose={closeTermsModal}
                    />
                </>
            )}
        </div>
    );
};

export default CustomerLogin;
